import type { DiaryResult } from '@creanizer/types'
import { Calendar, Cloud, Heart } from 'lucide-react'
import { getStylesForType } from '../utils/visualStyles'
import '../styles/documentStyles.css'

interface DiaryViewerProps {
  document: DiaryResult
  visualStyle?: string
}

export function DiaryViewer({ document, visualStyle = 'warm' }: DiaryViewerProps) {
  // データ検証
  if (!document || !document.date || !document.body) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">日記</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // 天気アイコン取得
  const getWeatherIcon = (weather?: string): string => {
    if (!weather) return '📝'
    const w = weather.toLowerCase()
    if (w.includes('晴')) return '☀️'
    if (w.includes('曇')) return '☁️'
    if (w.includes('雨')) return '🌧️'
    if (w.includes('雪')) return '❄️'
    if (w.includes('嵐') || w.includes('台風')) return '🌪️'
    return '🌤️'
  }
  
  // 気分アイコン取得
  const getMoodIcon = (mood?: string): string => {
    if (!mood) return ''
    const m = mood.toLowerCase()
    if (m.includes('楽') || m.includes('嬉') || m.includes('幸')) return '😊'
    if (m.includes('悲') || m.includes('寂')) return '😢'
    if (m.includes('怒') || m.includes('憤')) return '😠'
    if (m.includes('不安') || m.includes('心配')) return '😟'
    if (m.includes('疲')) return '😩'
    if (m.includes('穏') || m.includes('平')) return '😌'
    return '😐'
  }
  
  // 本文の段落分割
  const formatBody = (body: string): string[] => {
    return body.split('\n').filter(p => p.trim())
  }
  
  // ビジュアルスタイルの取得
  const styles = getStylesForType('diary')
  const selectedStyle = styles.find(s => s.id === visualStyle) || styles[0]
  const styleClass = selectedStyle.className
  
  // スタイル別のレンダリング
  if (visualStyle === 'cold') {
    // 無機質な手記スタイル
    return (
      <div className="max-w-3xl mx-auto">
        <div className={`rounded-lg p-6 ${styleClass}`}>
          <div className="mb-4 pb-2 border-b border-gray-400">
            <div className="flex justify-between items-center">
              <div className="font-mono text-sm">
                DATE: {document.date}
              </div>
              {document.weather && (
                <div className="font-mono text-sm">
                  WEATHER: {document.weather}
                </div>
              )}
              {document.mood && (
                <div className="font-mono text-sm">
                  STATUS: {document.mood}
                </div>
              )}
            </div>
          </div>
          <div className="font-mono text-sm leading-relaxed">
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="mb-3">
                [{String(index + 1).padStart(3, '0')}] {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
    )
  }
  
  if (visualStyle === 'horror') {
    // ホラー風の不穏な日記
    return (
      <div className="max-w-3xl mx-auto">
        <div className={`rounded-lg p-8 relative ${styleClass}`}>
          <div className="mb-6">
            <div className="text-red-950/80 font-serif">
              <div className="text-lg mb-2" style={{ fontFamily: 'Courier New, monospace' }}>
                {document.date}
              </div>
              <div className="flex gap-4 text-sm opacity-70">
                {document.weather && <span>天候: {document.weather}</span>}
                {document.mood && <span>心境: {document.mood}</span>}
              </div>
            </div>
          </div>
          <div className="space-y-4 text-red-950/90" style={{ fontFamily: 'Georgia, serif' }}>
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="leading-relaxed" style={{
                textShadow: '0 0 2px rgba(139, 0, 0, 0.1)',
                transform: `rotate(${Math.random() * 0.5 - 0.25}deg)`
              }}>
                {paragraph}
              </p>
            ))}
          </div>
          <div className="absolute top-4 right-4 text-4xl opacity-10 rotate-12">
            🩸
          </div>
        </div>
      </div>
    )
  }
  
  if (visualStyle === 'aged') {
    // 古びた日記スタイル
    return (
      <div className="max-w-3xl mx-auto">
        <div className={`rounded-lg p-8 ${styleClass}`}>
          <div className="border-b-2 border-yellow-800/30 pb-4 mb-6">
            <div className="text-yellow-950 font-serif text-xl">
              {document.date}
            </div>
            <div className="flex gap-4 text-sm text-yellow-900/70 mt-2">
              {document.weather && <span>{getWeatherIcon(document.weather)} {document.weather}</span>}
              {document.mood && <span>{getMoodIcon(document.mood)} {document.mood}</span>}
            </div>
          </div>
          <div className="space-y-4 text-yellow-950/90 font-serif leading-relaxed">
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="text-base">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
    )
  }
  
  // デフォルト（温かい日記）
  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg shadow-lg overflow-hidden">
        {/* 日記ヘッダー（ノート風） */}
        <div className="bg-gradient-to-r from-orange-100 to-yellow-100 border-b-2 border-orange-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-orange-600" />
              <h2 className="text-2xl font-handwriting text-orange-900">
                {document.date}
              </h2>
            </div>
            
            <div className="flex items-center gap-4">
              {document.weather && (
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getWeatherIcon(document.weather)}</span>
                  <span className="text-sm text-orange-700">{document.weather}</span>
                </div>
              )}
              
              {document.mood && (
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getMoodIcon(document.mood)}</span>
                  <span className="text-sm text-orange-700">{document.mood}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* 日記本文（ノート罫線風） */}
        <div className="p-8 relative">
          {/* 左側の穴（ノート風装飾） */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-red-300"></div>
          <div className="absolute left-2 top-8 w-4 h-4 rounded-full border-2 border-gray-400 bg-white"></div>
          <div className="absolute left-2 top-24 w-4 h-4 rounded-full border-2 border-gray-400 bg-white"></div>
          <div className="absolute left-2 top-40 w-4 h-4 rounded-full border-2 border-gray-400 bg-white"></div>
          
          {/* 本文 */}
          <div className="ml-8 space-y-4 font-handwriting text-gray-800">
            {formatBody(document.body).map((paragraph, index) => (
              <div
                key={index}
                className="relative pb-4 border-b border-gray-200 last:border-b-0"
                style={{
                  backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 1.5rem, #e5e7eb 1.5rem, #e5e7eb calc(1.5rem + 1px))',
                  backgroundSize: '100% 1.5rem',
                  lineHeight: '1.5rem'
                }}
              >
                <p className="text-lg leading-relaxed tracking-wide">
                  {paragraph}
                </p>
              </div>
            ))}
          </div>
          
          {/* ページ装飾 */}
          <div className="mt-8 flex justify-end">
            <div className="text-xs text-gray-400 italic">
              Page {Math.floor(Math.random() * 100) + 1}
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Klee+One:wght@400;600&display=swap');
        
        .font-handwriting {
          font-family: 'Klee One', cursive;
        }
      `}</style>
    </div>
  )
}